// Safe date parsing and formatting utilities
import { format as dateFnsFormat } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';

/**
 * Safely parse a date value
 * @param {string|Date|number} dateValue - The date value to parse
 * @returns {Date|null} - Valid Date object or null
 */
export const safeParseDate = (dateValue) => {
  if (!dateValue) return null;
  
  try {
    // If it's already a valid Date
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }
    
    // If it's a string
    if (typeof dateValue === 'string') {
      // Check for common invalid values
      if (dateValue === 'undefined' || dateValue === 'null' || dateValue === 'Invalid Date') {
        return null;
      }
      
      const parsed = new Date(dateValue);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    
    // If it's a number (timestamp)
    if (typeof dateValue === 'number') {
      const parsed = new Date(dateValue);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    
    return null;
  } catch (error) {
    console.warn('Error parsing date:', dateValue, error);
    return null;
  }
};

/**
 * Safely format a date using date-fns
 * @param {string|Date|number} dateValue - The date value to format
 * @param {string} formatString - The format string (default: 'yyyy-MM-dd')
 * @param {object} options - Additional options (locale, etc.)
 * @returns {string} - Formatted date string or fallback
 */
export const safeFormat = (dateValue, formatString = 'yyyy-MM-dd', options = {}) => {
  const date = safeParseDate(dateValue);
  if (!date) return options.fallback || '-';
  
  try {
    return dateFnsFormat(date, formatString, {
      locale: options.locale || ar,
      ...options
    });
  } catch (error) {
    console.warn('Error formatting date:', dateValue, error);
    return options.fallback || '-';
  }
};

/**
 * Safely format a date to locale string
 * @param {string|Date|number} dateValue - The date value to format
 * @param {string} locale - The locale (default: 'ar-AE')
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted date string or fallback
 */
export const safeToLocaleString = (dateValue, locale = 'ar-AE', options = {}) => {
  const date = safeParseDate(dateValue);
  if (!date) return options.fallback || '-';
  
  try {
    return date.toLocaleString(locale, options);
  } catch (error) {
    console.warn('Error converting date to locale string:', dateValue, error);
    return options.fallback || '-';
  }
};

/**
 * Safely format a date to locale date string
 * @param {string|Date|number} dateValue - The date value to format
 * @param {string} locale - The locale (default: 'ar-AE')
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted date string or fallback
 */
export const safeToLocaleDateString = (dateValue, locale = 'ar-AE', options = {}) => {
  const date = safeParseDate(dateValue);
  if (!date) return options.fallback || '-';
  
  try {
    return date.toLocaleDateString(locale, options);
  } catch (error) {
    console.warn('Error converting date to locale date string:', dateValue, error);
    return options.fallback || '-';
  }
};

export default {
  safeParseDate,
  safeFormat,
  safeToLocaleString,
  safeToLocaleDateString
};
